import { type FC, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton, Status, Typography } from 'shared/ui'
import { MenuSearch } from 'shared/ui/Menu/ui/MenuSearch/MenuSearch'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import styles from './styles.module.scss'

type IMenuTitleProps = {
  enableSearch: boolean
  enableCollapse: boolean
  openCollapse: boolean
  toggleCollapse: () => void
}

export const MenuTitle: FC<IMenuTitleProps> = observer(
  ({ enableCollapse, openCollapse, toggleCollapse, enableSearch }) => {
    const { smartViewsMenuStore } = useConversationMenuContext()
    const { onOpenModalCreate, search, setSearch } = smartViewsMenuStore
    const refWrap = useRef<HTMLDivElement>(null)
    const [wrapElement, setWrapElement] = useState<HTMLDivElement>()
    const [showSearch, setShowSearch] = useState<boolean>(false)
    const handleBlur = () => setShowSearch(false)
    const handleSearch = () => setShowSearch(true)

    useEffect(() => {
      if (refWrap.current) {
        setWrapElement(refWrap.current)
      }
    }, [])

    useEffect(() => {
      if (!enableSearch) {
        setSearch('')
      }
    }, [enableSearch])

    const onClickTitle = () => {
      toggleCollapse()
    }

    if (showSearch) {
      return (
        <div className={styles.searchWrap}>
          <MenuSearch
            onSearch={setSearch}
            onBlur={handleBlur}
            search={search}
            autofocus={true}
            placeholder={'Search smart views'}
            variant={'transparent'}
          />
        </div>
      )
    }

    return (
      <div ref={refWrap} className={styles.wrap}>
        <div className={styles.contentWrap} onClick={onClickTitle}>
          <div className={styles.titleWrap}>
            <Typography
              className={styles.boxTitle}
              variant={'button-small'}
              tag={'div'}
              ariaLabel={'boxTitle'}
            >
              Smart views
            </Typography>
            {enableCollapse && (
              <div className={styles.boxArrow}>
                <IconButton
                  icon={openCollapse ? 'chevronDown' : 'chevronRight'}
                  size={'origin_icon'}
                  fontSize={12}
                  color={'transparent'}
                  ariaLabel='MenuTitle_arrow'
                />
              </div>
            )}
          </div>

          <Status
            className={styles.statusLabel}
            title={'New'}
            intent={'purple'}
            emphasis={'high'}
            size={'small'}
          />
        </div>

        {enableSearch && (
          <div className={styles.searchIcon}>
            <IconButton
              ariaLabel='MenuTitle_search'
              icon={'search'}
              size={'small'}
              variant={'icon'}
              color={'tertiary'}
              tooltipProps={{
                placement: 'top',
                label: 'Search smart views',
                PopperProps: {
                  popperOptions: {
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: wrapElement,
                        },
                      },
                    ],
                  },
                },
              }}
              onClick={handleSearch}
            />
          </div>
        )}

        <div className={styles.add}>
          <IconButton
            ariaLabel={'menu_title_add'}
            icon={'add'}
            size={'small'}
            variant={'icon'}
            color={'tertiary'}
            tooltipProps={{
              label: 'Create smart view',
              placement: 'top',
              PopperProps: {
                popperOptions: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: wrapElement,
                      },
                    },
                  ],
                },
              },
            }}
            onClick={onOpenModalCreate}
          />
        </div>
      </div>
    )
  }
)
